import { useEffect, useRef, useState } from 'react';
import Button from '../../../../components/forms/button.component';
import { useForm } from 'react-hook-form';
import useModal from '../../../../hooks/use-close-modal.hook';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/forms/icon.component';
import { ChannelsService } from '../../../../services/channels.service';
import SimpleTitle from '../../../../components/forms/simple-titles.component';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { AlertService } from '../../../../services/alert.service';
import LogData from '../../../../components/logs/log-data.component';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDarkMode } from '../../../../hooks/use-dark-mode.hook';
interface SendMessageModalProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload?: any;
}
interface Operator {
  name: string;
  img: string;
  imgDark: string;
  width: string;
  height: string;
}
function SendMessageModalRCS({ show, onRequestClose, payload }: SendMessageModalProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [operatorPhones, setOperatorPhones] = useState<Record<string, string>>({});
  const [phoneErrors, setPhoneErrors] = useState<Record<string, string>>({});
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const isDarkTheme = useDarkMode();

  const operators: Operator[] = [
    {
      name: 'telcel',
      img: '/assets/media/operators/telcel.png',
      imgDark: '/assets/media/operators/telcel-dark.png',
      width: '60px',
      height: '15px'
    },
    {
      name: 'movistar',
      img: '/assets/media/operators/movistar.png',
      imgDark: '/assets/media/operators/movistar-dark.png',
      width: '60em',
      height: '15px'
    },
    {
      name: 'att',
      img: '/assets/media/operators/att.png',
      imgDark: '/assets/media/operators/att-dark.png',
      width: '55em',
      height: '22px'
    },
    {
      name: 'other',
      img: '/assets/media/operators/altan.png',
      imgDark: '/assets/media/operators/altan-dark.png',
      width: '50em',
      height: '22px'
    }
  ];

  const activeOperators: Operator[] = operators.filter(
    (op) => Array.isArray(payload?.operators) && payload.operators.includes(op.name)
  );

  useModal(modalRef, show);

  const defaultValues = { output: payload?._id || '' };

  const {
    handleSubmit,
    reset,
    setValue,
    register,
    formState: { isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const handleCaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const closeModal = () => {
    onRequestClose(false);
    reset({ output: payload?._id || '' });
    setOperatorPhones({});
    setPhoneErrors({});
    recaptchaRef.current?.reset();
    setRecaptchaToken(null);
  };
  const tenDigitsRegex = /^\d{12}$/;
  const validatePhoneNumber = (phone: string) => tenDigitsRegex.test(phone);

  const handleOperatorPhoneChange = (operatorName: string, newValue: string) => {
    const digitsOnly = newValue.replace(/\D/g, '').slice(0, 12);

    setOperatorPhones((prev) => ({
      ...prev,
      [operatorName]: digitsOnly
    }));

    if (digitsOnly.length === 0 || (digitsOnly.startsWith('52') && digitsOnly.length <= 2)) {
      setPhoneErrors((prev) => {
        const copy = { ...prev };
        delete copy[operatorName];
        return copy;
      });
      return;
    }

    if (digitsOnly.startsWith('52') && digitsOnly.length <= 2) {
      console.log('Sii');
    }

    if (!tenDigitsRegex.test(digitsOnly)) {
      setPhoneErrors((prev) => ({
        ...prev,
        [operatorName]: 'Debe contener exactamente 10 dígitos'
      }));
    } else {
      setPhoneErrors((prev) => {
        const copy = { ...prev };
        delete copy[operatorName];
        return copy;
      });
    }
  };
  const hasValidPhoneNumber = () => {
    return Object.values(operatorPhones).some((num) => validatePhoneNumber(num));
  };

  useEffect(() => {
    setValue('output', payload?._id || '');
  }, [payload, setValue]);
  register('output', { required: true });

  const onSubmit = async (data: { output: string }) => {
    if (!recaptchaToken) {
      AlertService.toastInfo(t('general.recaptcha-required'));
      return;
    }
    if (!hasValidPhoneNumber()) {
      AlertService.error(
        t('general.atention'),
        'Debe agregar al menos un número de teléfono válido'
      );
      return;
    }

    const operatorPhoneNumbers: { phone_number: string; operator: string }[] = [];
    Object.entries(operatorPhones).forEach(([operatorName, phone]) => {
      if (validatePhoneNumber(phone)) {
        operatorPhoneNumbers.push({
          operator: operatorName,
          phone_number: phone
        });
      }
    });

    const submitData: any = {
      output: data.output,
      operatorPhoneNumbers,
      recaptcha: recaptchaToken
    };

    const typeArray = Array.isArray(payload?.type) ? payload.type : [payload?.type].filter(Boolean);
    const messageCount = typeArray.length;
    const messageText =
      messageCount === 1
        ? t('channels.rcs.send-message.a-message')
        : t('channels.rcs.send-message.messages');

    const html = `
      <b>${t('channels.rcs.send-message.question')}</b><br/>
      <p>Se enviará a <strong>${operatorPhoneNumbers.length} ${
        operatorPhoneNumbers.length === 1 ? 'número' : 'números'
      }</strong></p>
    `;
    const isConfirmed = await AlertService.question(t('general.atention'), html + messageText, () =>
      Promise.resolve(true)
    );
    if (!isConfirmed) return;

    try {
      const response = await ChannelsService.sendMessageRCS(submitData);
      if (!response.status) {
        const formattedErrors = response.errors
          .map((errorObj: Record<string, string>) => {
            const errorEntries = Object.entries(errorObj).filter(([key]) => key !== 'message');

            let errorDetails = '';
            if (errorEntries.length > 0) {
              const [key, value] = errorEntries[0];
              const translatedKey = t('templates.rcs.' + key.toLowerCase());
              errorDetails = `<strong>${translatedKey}:</strong> ${value}`;
            }

            const message = errorObj.message;
            if (message) {
              errorDetails += errorDetails ? `<br/><em>${message}</em>` : `<em>${message}</em>`;
            }

            return `<div class="mb-4">${errorDetails}</div>`;
          })
          .join('');
        const errorMessage = `<div class="mb-2">${t('channels.rcs.send-message.error')}</div><br/>${formattedErrors}`;
        AlertService.error(t('general.atention'), errorMessage);
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      } else {
        AlertService.toastSuccess(t('channels.rcs.send-message.success'));
        LogData(submitData, 'sendRCSMessage', window.location.href);
        onRequestClose(true);
        reset({ output: payload?._id || '' });
        setOperatorPhones({});
        setPhoneErrors({});
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      }
    } catch (error: any) {
      console.error('Error:', error);
      AlertService.toastError(error.error || error);
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }
  };

  const anyInvalid = Object.keys(phoneErrors).length > 0;

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog modal-dialog-centered mw-950px">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => closeModal()}
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1 className="mb-3">
                  {t('channels.rcs.send-message-text')} <br />
                  <span className="link-primary fw-bold ms-2">{payload?.name}</span>
                </h1>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
              </div>
              <div className="separator border-2 my-5"></div>

              <div className="d-flex justify-content-center row">
                <div className="col-lg-6 col-12 mb-10 mb-lg-0  h-100 p-10 pt-5">
                  <SimpleTitle
                    title="Números de teléfono por operador"
                    subtitle="(Al menos uno es requerido)"
                    type="info"
                  />
                  <div className="row">
                    <div className="fv-row mb-10">
                      <div className="bg-light-info rounded p-4 mb-4">
                        <h6 className="text-info mb-3">
                          <Icon icon="ki-phone" size="fs-4" ClassName="me-2" />
                          Operadores Activos
                        </h6>

                        {activeOperators.map((operator: any, i: number) => {
                          const keyName = operator.name;
                          const val = operatorPhones[keyName] || '';
                          const errorMsg = phoneErrors[keyName];

                          return (
                            <div key={i} className="my-4">
                              <div className="mb-3 d-flex align-items-center gap-3">
                                <span className="theme-light-show">
                                  <img
                                    src={operator.img}
                                    width={operator?.width}
                                    height={operator?.height}
                                  />
                                </span>
                                <span className="theme-dark-show">
                                  <img
                                    src={operator?.imgDark}
                                    width={operator?.width}
                                    height={operator?.height}
                                  />
                                </span>
                              </div>
                              <PhoneInput
                                country="mx"
                                onlyCountries={['mx']}
                                placeholder="55 1234 5678"
                                value={val}
                                countryCodeEditable={false}
                                onChange={(value) => handleOperatorPhoneChange(keyName, value)}
                                inputClass="phone-input-custom bg-body"
                                containerClass="phone-input-container"
                                buttonClass="phone-input-button"
                                dropdownClass="phone-country-dropdown"
                                searchClass="search-box"
                              />

                              {errorMsg && (
                                <div className="invalid-feedback d-block">{errorMsg}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-12 mb-10 mb-lg-0  h-100 p-10 mt-15">
                  {Array.isArray(payload?.type) && payload.type.length > 0 && (
                    <div className="fv-row mb-10">
                      <div className="d-flex flex-column align-items-start bg-light-primary rounded p-5 mt-2 mb-1">
                        <div className="text-center w-100 mb-3">
                          <b>{t('channels.rcs.send-message.supported')}</b>
                        </div>
                        {payload.type.map((type: string) => {
                          const lower = type.toLowerCase();
                          let iconClass = 'fw-bolder';
                          if (lower === 'single') {
                            iconClass = 'info';
                          } else if (lower === 'conversational') {
                            iconClass = 'primary';
                          }
                          return (
                            <div
                              key={type}
                              className="d-flex justify-content-start align-items-start gap-3 mb-1"
                            >
                              <Icon
                                icon="ki-messages"
                                size="fs-2x"
                                className="hover-scale"
                                type={iconClass}
                              />
                              <span className="fw-semibold">{t(`templates.rcs.${lower}`)}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-3 text-center fs-5">
                        <strong>
                          {t('channels.rcs.send-message.message-text', {
                            message: payload?.type.length
                          })}
                        </strong>
                        <br />
                        {Array.isArray(payload?.type) && payload.type.length === 1
                          ? t('channels.rcs.send-message.a-message')
                          : t('channels.rcs.send-message.messages')}
                      </div>
                      <div className="col-12 d-flex justify-content-center mt-5">
                        <ReCAPTCHA
                          sitekey="6Lfec20rAAAAANyGkUlot_jUEWAbexPdSk8Nw_lu"
                          onChange={handleCaptchaChange}
                          theme={isDarkTheme ? 'dark' : 'light'}
                          ref={recaptchaRef}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex flex-center flex-row-fluid">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => closeModal()}
                >
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  id="sendData"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={!hasValidPhoneNumber() || isSubmitting || anyInvalid || !recaptchaToken}
                  isLoading={isSubmitting}
                >
                  {t('general.send')}
                  <Icon icon="ki-send" size="fs-3" className="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SendMessageModalRCS;
